import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  CalendarDays,
  CalendarClock,
  FileText,
  Sparkles,
  AlertTriangle,
  CheckCircle2,
  ClipboardList,
  Lightbulb,
  Clock,
  Bot,
  Download,
  Building2,
  FileWarning,
  RefreshCw,
  Calculator,
  ExternalLink,
} from "lucide-react";
import { api, API } from "../lib/api";
import RiskBadge from "../components/RiskBadge";
import SourceLanguageBadge from "../components/SourceLanguageBadge";
import TagsEditor from "../components/TagsEditor";
import InsuranceAnalysisView from "../components/InsuranceAnalysisView";
import DocumentView from "../components/DocumentView";
import LeaseEnrichmentPanel from "../components/LeaseEnrichmentPanel";
import InsuranceEnrichmentPanel from "../components/InsuranceEnrichmentPanel";
import FinancingEnrichmentPanel from "../components/FinancingEnrichmentPanel";
import ContractEnrichmentPanel from "../components/ContractEnrichmentPanel";
import PermitEnrichmentPanel from "../components/PermitEnrichmentPanel";
import FinancialsEnrichmentPanel from "../components/FinancialsEnrichmentPanel";
import AlertsBanner from "../components/AlertsBanner";
import ChatFab from "../components/ChatFab";
import CompareLauncher from "../components/CompareLauncher";
import PreSignatureLauncher from "../components/PreSignatureLauncher";
import DecisionLauncher from "../components/DecisionLauncher";
import DocumentTimeline from "../components/DocumentTimeline";
import LineageSuggestionBanner from "../components/LineageSuggestionBanner";
import InvoicePaymentStatusPanel from "../components/analysis/InvoicePaymentStatusPanel";
import InvoiceTaxBadge from "../components/analysis/InvoiceTaxBadge";
import { ExtractionBadges, NeedsReviewBanner, DateCitation } from "../components/ExtractionBadges";
import { Button } from "../components/ui/button";
import { useAuth } from "../lib/AuthContext";
import { useTranslation } from "react-i18next";
import { useLocaleFormat } from "../i18n/useLocaleFormat";
import { toast } from "sonner";

export default function Analysis() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfError, setPdfError] = useState(false);
  const [pdfSearchTerm, setPdfSearchTerm] = useState("");
  const [pdfReloadNonce, setPdfReloadNonce] = useState(0);
  const [launching, setLaunching] = useState(false);
  const { user } = useAuth();
  const { t } = useTranslation("analysis");
  const { t: tInvoices } = useTranslation("invoices");
  const { formatDate } = useLocaleFormat();

  // Live-poll status whenever the doc is mid-flight (queued/analyzing) so the
  // user sees a fresh run land without refreshing.
  useEffect(() => {
    if (!doc) return;
    if (doc.status !== "queued" && doc.status !== "analyzing") return;
    const t = setInterval(async () => {
      try {
        const res = await api.get(`/documents/${id}`);
        setDoc(res.data);
        if (res.data.status === "completed") {
          toast.success("Re-analysis complete");
        } else if (res.data.status === "failed") {
          toast.error(res.data.analysis_error || "Analysis failed");
        }
      } catch {
        /* ignore polling errors */
      }
    }, 2000);
    return () => clearInterval(t);
  }, [doc?.status, id]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(`/documents/${id}`);
        setDoc(res.data);
      } catch (err) {
        console.error(err);
        toast.error("Document not found");
        navigate("/dashboard");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="text-sm text-zinc-400 py-20 text-center" data-testid="analysis-loading">
        Loading analysis…
      </div>
    );
  }

  if (!doc) return null;

  const a = doc.analysis;
  const hasAnalysis = !!a;
  const docType = doc.document_type || "lease";
  const isInsurance = docType === "insurance";
  const isLease = docType === "lease";
  const isCompleted = doc.status === "completed" && hasAnalysis;
  const isNeedsReview = doc.status === "needs_review";
  // v2 documents have a populated `universal` block. Anything migrated by the
  // startup script also has it, so this path covers the entire backlog.
  const hasUniversal = !!doc.universal && doc.schema_version === "v2";
  const ENRICHMENT_PANEL_BY_TYPE = {
    lease: LeaseEnrichmentPanel,
    insurance: InsuranceEnrichmentPanel,
    financing: FinancingEnrichmentPanel,
    contract: ContractEnrichmentPanel,
    permit: PermitEnrichmentPanel,
    financials: FinancialsEnrichmentPanel,
  };
  const EnrichmentPanel = ENRICHMENT_PANEL_BY_TYPE[docType] || LeaseEnrichmentPanel;
  const ANALYSIS_HEADLINE_KEY_BY_TYPE = {
    lease: "headline_lease",
    insurance: "headline_insurance",
    financing: "headline_financing",
    contract: "headline_contract",
    permit: "headline_permit",
    financials: "headline_financials",
    other: "headline_other",
  };
  const headlineKey = ANALYSIS_HEADLINE_KEY_BY_TYPE[docType] || "headline_other";
  const analysisHeadline = t(headlineKey);
  const isReanalyzing =
    hasAnalysis && (doc.status === "queued" || doc.status === "analyzing");
  // Show the analysis whenever we have one — even while a fresh re-run is in flight.
  // needs_review docs intentionally HIDE the analysis surface.
  const isAnalyzed = (isCompleted || isReanalyzing) && !isNeedsReview;
  const isManualEntry = doc.ingestion_source === "manual";
  const basePdfSrc = user && !isManualEntry
    ? `${API}/documents/${id}/file`
    : null;
  // Mutable iframe src — citation jumps update the #search fragment to leverage
  // the browser's built-in PDF.js viewer text-search/highlight.
  const pdfSrc = basePdfSrc
    ? `${basePdfSrc}#view=FitH${pdfSearchTerm ? `&search=${encodeURIComponent(pdfSearchTerm)}` : ""}`
    : null;
  // Deterministic iframe key — increments on every citation jump to force a hard remount
  // even if the resulting #search string happens to be identical to the previous one.
  const pdfIframeKey = `${pdfSrc || "none"}::${pdfReloadNonce}`;
  const handleCitationJump = (citation) => {
    if (!citation?.quote) return;
    // Truncate to ~80 chars — PDF.js native search works best on shorter phrases
    const phrase = citation.quote.slice(0, 80).trim();
    setPdfSearchTerm(phrase);
    setPdfReloadNonce((n) => n + 1);
    // Scroll the PDF column into view (desktop layout)
    const pane = document.querySelector('[data-testid="pdf-viewer-iframe"]');
    if (pane && pane.scrollIntoView) pane.scrollIntoView({ behavior: "smooth", block: "center" });
  };
  const reportHref = user
    ? `${API}/documents/${id}/report`
    : null;

  return (
    <div className="animate-fade-up" data-testid="analysis-page">
      <ChatFab
        documentId={id}
        documentType={docType}
        cachedSuggestions={doc?.enrichment?.suggested_questions}
        onCitationJump={handleCitationJump}
      />
      <Link
        to="/dashboard"
        data-testid="back-to-dashboard-link"
        className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 transition-colors mb-6"
      >
        <ArrowLeft size={14} /> {t("back_to_dashboard")}
      </Link>

      <LineageSuggestionBanner docId={id} />
      <DocumentTimeline docId={id} />

      {/* Live alerts pinned to this document */}
      <AlertsBanner documentId={id} />

      {/* Needs-Review banner — shown when extraction failed; replaces analysis surface */}
      {isNeedsReview && (
        <NeedsReviewBanner
          isReanalyzing={false}
          onReanalyze={async () => {
            try {
              await api.post(`/documents/${id}/reanalyze?force_ocr=true`);
              toast.success(t("toasts.rerun_queued"));
              setTimeout(async () => {
                try {
                  const r = await api.get(`/documents/${id}`);
                  setDoc(r.data);
                } catch { /* swallow */ }
              }, 500);
            } catch (e) {
              toast.error(e.response?.data?.detail || t("toasts.rerun_failed"));
            }
          }}
        />
      )}

      {/* Header */}
      <div className="bg-white border border-zinc-200 rounded-xl p-6 sm:p-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4 min-w-0">
            <div className="w-12 h-12 rounded-lg bg-zinc-100 grid place-items-center shrink-0">
              <FileText size={20} className="text-zinc-700" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">
                {analysisHeadline}
              </p>
              <h1
                className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-950 break-all"
                data-testid="analysis-filename"
              >
                {doc.filename}
              </h1>
              <p className="text-xs text-zinc-500 mt-2">
                {t("uploaded_on", { date: formatDate(doc.created_at) })}
              </p>
            </div>
          </div>

          <div className="flex flex-col items-end gap-2">
            {isAnalyzed && (
              <>
                <RiskBadge level={a.risk_score} size="lg" testId="analysis-risk-badge" />
                <SourceLanguageBadge sourceLanguage={doc.source_language || (doc.universal || {}).source_language} />
                <ExtractionBadges extractionCache={doc.extraction_cache} analysis={a} />
                {doc.analysis_used_fallback && !isNeedsReview && (
                  <span
                    data-testid="ai-partial-badge"
                    title={t("badges.ai_partial_title")}
                    className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800"
                  >
                    <Bot size={13} strokeWidth={1.75} className="text-amber-600" />
                    {t("badges.ai_partial")}
                  </span>
                )}
                {reportHref && (
                  <a
                    href={reportHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="export-pdf-button"
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-zinc-950 hover:bg-zinc-800 text-white text-xs font-medium transition-colors"
                  >
                    <Download size={13} strokeWidth={2} /> {t("actions.export_pdf")}
                  </a>
                )}
                <button
                  onClick={async () => {
                    try {
                      await api.post(`/documents/${id}/reanalyze`);
                      toast.success(t("toasts.rerun_queued"));
                      // Re-fetch shortly so user sees the status transition
                      setTimeout(async () => {
                        try {
                          const r = await api.get(`/documents/${id}`);
                          setDoc(r.data);
                        } catch { /* swallow */ }
                      }, 500);
                    } catch (e) {
                      const detail = e.response?.data?.detail;
                      if (e.response?.status === 402) {
                        toast.error(detail || t("toasts.monthly_limit"));
                      } else if (e.response?.status === 409) {
                        toast.message(detail || t("toasts.rerun_in_progress"));
                      } else {
                        toast.error(detail || t("toasts.rerun_failed"));
                      }
                    }
                  }}
                  disabled={isReanalyzing}
                  data-testid="reanalyze-button"
                  title={t("actions.rerun_title")}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-zinc-200 bg-white hover:bg-zinc-50 text-zinc-700 text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RefreshCw size={13} strokeWidth={2} className={isReanalyzing ? "animate-spin" : ""} /> {isReanalyzing ? t("actions.rerunning") : t("actions.rerun")}
                </button>
                <CompareLauncher doc={doc} />
                <PreSignatureLauncher doc={doc} />
                <DecisionLauncher doc={doc} />
              </>
            )}
          </div>
        </div>

        {isAnalyzed && isLease && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mt-8 pt-6 border-t border-zinc-200">
              <Meta
                Icon={CalendarDays}
                label={t("meta.signing_date", "Signing date")}
                value={a.signing_date ? new Date(a.signing_date).toLocaleDateString() : "—"}
                testId="meta-signing-date"
                citation={(a.date_citations || []).find((c) => c.field === "signing_date")}
              />
              <Meta
                Icon={CalendarDays}
                label={t("meta.start_date", "Start date")}
                value={a.start_date ? new Date(a.start_date).toLocaleDateString() : "—"}
                testId="meta-start-date"
                citation={(a.date_citations || []).find((c) => c.field === "start_date")}
              />
              <Meta
                Icon={CalendarClock}
                label={t("meta.end_date", "Expiration")}
                value={a.end_date ? new Date(a.end_date).toLocaleDateString() : "—"}
                testId="meta-end-date"
                citation={(a.date_citations || []).find((c) => c.field === "end_date")}
              />
              <Meta
                Icon={Clock}
                label={t("meta.duration", "Duration")}
                value={a.duration_months ? `${a.duration_months} ${t("meta.months", "months")}` : "—"}
                testId="meta-duration"
              />
              <Meta
                Icon={Building2}
                label={t("meta.lease_type", "Lease type")}
                value={a.lease_type || "—"}
                testId="meta-lease-type"
              />
            </div>
            {(a.renewal_options && a.renewal_options.length > 0) && (
              <div className="mt-4 px-3 py-2.5 rounded-lg bg-emerald-50/50 border border-emerald-100 text-[13px] text-emerald-900" data-testid="renewal-options">
                <strong className="font-semibold">{t("meta.renewal_option", "Renewal option")}: </strong>
                {a.renewal_options.map((r, i) => (
                  <span key={i}>
                    {r.duration_months ? `${r.duration_months} ${t("meta.months", "months")}` : ""}
                    {r.notice_required_days ? ` · ${r.notice_required_days} ${t("meta.days_notice", "days notice")}` : ""}
                    {r.description ? ` — ${r.description}` : ""}
                    {i < a.renewal_options.length - 1 ? " · " : ""}
                  </span>
                ))}
              </div>
            )}
            <div className="mt-6 pt-6 border-t border-zinc-200">
              <TagsEditor
                docId={doc.id}
                value={doc.tags || []}
                onChange={(tags) => setDoc((d) => ({ ...d, tags }))}
              />
            </div>
          </>
        )}
        {isAnalyzed && !isLease && (
          <div className="mt-6 pt-6 border-t border-zinc-200">
            <TagsEditor
              docId={doc.id}
              value={doc.tags || []}
              onChange={(tags) => setDoc((d) => ({ ...d, tags }))}
            />
          </div>
        )}
      </div>

      {!isAnalyzed ? (
        <div className="mt-6 space-y-6">
          {docType === "invoice" && (
            <InvoicePaymentStatusPanel
              docId={doc.id}
              currentStatus={doc.enrichment?.payment_status}
              onUpdated={() => {
                api.get(`/documents/${doc.id}`)
                  .then((r) => setDoc(r.data))
                  .catch(() => {});
              }}
            />
          )}
          {docType === "invoice" && <InvoiceTaxBadge enrichment={doc.enrichment} />}
          <div className="bg-white border border-zinc-200 rounded-xl p-10 text-center" data-testid="not-analyzed">
            {/* Mid-analysis state: doc is already queued/analyzing — show only
                the spinner. The page-level polling effect (line ~62) takes care
                of refreshing once the status flips to completed/failed. */}
            {(doc.status === "queued" || doc.status === "analyzing") ? (
              <div className="flex flex-col items-center gap-3" data-testid="not-analyzed-pending">
                <RefreshCw size={28} className="animate-spin text-zinc-400" strokeWidth={1.5} />
                <p className="text-sm text-zinc-600 font-medium">
                  {t("not_analyzed.launching")}
                </p>
                <p className="text-[12px] text-zinc-400">
                  {t("uploaded_on", { date: doc.created_at ? formatDate(doc.created_at) : "—" })}
                </p>
              </div>
            ) : (
              <>
                <p className="text-sm text-zinc-600 mb-5">
                  {t("not_analyzed.message")}
                </p>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 justify-center">
                  <Button
                    onClick={async () => {
                      setLaunching(true);
                      try {
                        const res = await api.post(`/documents/${id}/analyze`);
                        // Backend returns 202 + updated doc (status=queued or
                        // cached doc if already completed). Either way: feed
                        // it into state and let the live-poll effect take over.
                        setDoc(res.data);
                        toast.success(t("not_analyzed.launching"));
                      } catch (err) {
                        const detail = err?.response?.data?.detail || err?.message;
                        toast.error(`${t("not_analyzed.launch_failed")} — ${detail}`);
                      } finally {
                        setLaunching(false);
                      }
                    }}
                    disabled={launching}
                    data-testid="launch-analysis-button"
                    className="h-10 px-5 bg-zinc-950 hover:bg-zinc-800 text-white rounded-lg inline-flex items-center gap-2 disabled:opacity-60"
                  >
                    {launching
                      ? <><RefreshCw size={14} className="animate-spin" /> {t("not_analyzed.launching")}</>
                      : <><Sparkles size={14} /> {t("not_analyzed.launch_button")}</>}
                  </Button>
                  <Button
                    onClick={() => navigate("/upload")}
                    variant="outline"
                    data-testid="go-to-upload-button"
                    className="h-10 px-5 rounded-lg border border-zinc-300 text-zinc-700 bg-white hover:bg-zinc-50"
                  >
                    {t("not_analyzed.go_to_upload")}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      ) : (
        <>
          {isReanalyzing && (
            <div
              data-testid="reanalyzing-banner"
              className="mt-6 px-4 py-3 rounded-xl border border-blue-200 bg-blue-50 text-sm text-blue-900 flex items-center gap-3"
            >
              <RefreshCw size={14} className="animate-spin shrink-0" />
              <span>
                Re-analyzing this document in the background — showing the previous result for now.
                You&apos;ll get a toast and a refresh when the new run completes.
              </span>
            </div>
          )}
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* PDF viewer (sticky on desktop) */}
          <aside
            className="lg:col-span-2 lg:sticky lg:top-6 lg:self-start"
            data-testid="pdf-viewer-section"
          >
            <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200">
                <div className="flex items-center gap-2">
                  <FileText size={14} className="text-zinc-600" />
                  <h2 className="text-xs uppercase tracking-[0.18em] text-zinc-500 font-medium">
                    {t("pdf.section_title")}
                  </h2>
                </div>
                {/* Iter69's centered "Ouvrir le document" CTA in the card body
                    (below) is the single source-PDF affordance. A redundant
                    header "Ouvrir ↗" link that pointed to the same pdfSrc was
                    removed to avoid two identical buttons. */}
              </div>
              {pdfSrc && !pdfError ? (
                // Inline preview of the original PDF via the browser's built-in
                // viewer. download_document now sets X-Frame-Options:
                // SAMEORIGIN + CSP frame-ancestors 'self' so this same-origin
                // /file response can be framed by the app. onError degrades to
                // the error branch below (which offers open-in-new-tab).
                <div data-testid="pdf-viewer-open">
                  <iframe
                    key={pdfIframeKey}
                    src={pdfSrc}
                    title={t("pdf.section_title")}
                    data-testid="pdf-viewer-iframe"
                    className="w-full bg-zinc-50 block"
                    style={{ height: "720px", border: "0" }}
                    onError={() => setPdfError(true)}
                  />
                  <div className="px-4 py-2 border-t border-zinc-200 text-center">
                    <a
                      href={pdfSrc}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="pdf-viewer-open-button"
                      className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1.5"
                    >
                      <ExternalLink size={12} strokeWidth={1.75} />
                      {t("pdf.open_button")}
                    </a>
                  </div>
                </div>
              ) : isManualEntry ? (
                <div className="p-10 text-center" data-testid="pdf-viewer-manual">
                  <Calculator size={22} className="text-indigo-600 mx-auto mb-2" strokeWidth={1.5} />
                  <p className="text-sm text-zinc-800 font-medium">{t("pdf.manual_entry_title")}</p>
                  <p className="text-xs text-zinc-500 mt-1.5 max-w-[260px] mx-auto leading-relaxed">
                    {t("pdf.manual_entry_subtitle")}
                  </p>
                </div>
              ) : (
                <div className="p-10 text-center" data-testid="pdf-viewer-error">
                  <FileWarning size={20} className="text-zinc-400 mx-auto mb-2" />
                  <p className="text-xs text-zinc-500">{t("pdf.preview_unavailable")}</p>
                  {pdfSrc && (
                    <a
                      href={pdfSrc}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-zinc-950 underline mt-2 inline-block"
                    >
                      {t("actions.open_pdf_new_tab")}
                    </a>
                  )}
                </div>
              )}
            </div>
          </aside>

          {/* Right column — analysis content */}
          <div className="lg:col-span-3 space-y-6">
            {docType === "invoice" && !isManualEntry &&
              (
                // "Low confidence" heuristic: completed doc but enrichment is
                // missing the two anchor fields (vendor_name + total_amount).
                // Typical for image-only scans / OCR failures.
                isCompleted &&
                (!doc.enrichment?.vendor_name || doc.enrichment?.total_amount == null)
              ) && (
              <div
                className="rounded-xl border border-qenva-cat-contract/30 bg-qenva-cat-contract/5 p-4 flex items-start gap-3"
                data-testid="invoice-low-confidence-banner"
              >
                <AlertTriangle size={18} strokeWidth={2} className="text-qenva-cat-contract mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-qenva-cat-contract">
                    {tInvoices("manual_form.low_confidence_banner.title")}
                  </p>
                  <p className="text-xs text-zinc-700 mt-0.5 leading-relaxed">
                    {tInvoices("manual_form.low_confidence_banner.subtitle")}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => navigate("/invoices/new", { state: {
                    prefill: doc.enrichment || {},
                    fromDocId: doc.id,
                  } })}
                  data-testid="invoice-low-confidence-cta"
                  className="px-3 py-1.5 rounded-md text-[12px] font-bold border border-qenva-cat-contract/40 text-qenva-cat-contract hover:bg-qenva-cat-contract/10 transition-colors shrink-0"
                >
                  {tInvoices("manual_form.low_confidence_banner.cta")}
                </button>
              </div>
            )}
            {docType === "invoice" && (
              <InvoicePaymentStatusPanel
                docId={doc.id}
                currentStatus={doc.enrichment?.payment_status}
                onUpdated={() => {
                  // Refetch the doc so the panel + alerts banner stay in sync
                  api.get(`/documents/${doc.id}`)
                    .then((r) => setDoc(r.data))
                    .catch(() => {});
                }}
              />
            )}
            {docType === "invoice" && <InvoiceTaxBadge enrichment={doc.enrichment} />}
            {hasUniversal ? (
              <DocumentView
                universal={doc.universal}
                enrichment={doc.enrichment}
                classification={doc.classification}
                EnrichmentPanel={EnrichmentPanel}
              />
            ) : isInsurance ? (
              <InsuranceAnalysisView analysis={a} />
            ) : (
            <>
            <section
              className="bg-white border border-zinc-200 rounded-xl p-6 sm:p-7"
              data-testid="summary-section"
            >
              <div className="flex items-center gap-2 mb-3">
                <Sparkles size={15} className="text-zinc-700" strokeWidth={1.75} />
                <h2 className="font-display font-semibold text-base tracking-tight">
                  {t("sections.executive_summary")}
                </h2>
              </div>
              <p className="text-[15px] leading-relaxed text-zinc-700">{a.summary}</p>
            </section>

            <Card
              title="Important clauses"
              Icon={ClipboardList}
              testId="clauses-section"
              accent="bg-zinc-100 text-zinc-700"
            >
              <ul className="space-y-3">
                {a.important_clauses.map((c, i) => (
                  <li key={i} className="flex gap-2.5 text-sm text-zinc-700 leading-relaxed">
                    <span className="mt-2 w-1 h-1 rounded-full bg-zinc-400 shrink-0" />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </Card>

            <Card
              title="Obligations"
              Icon={CheckCircle2}
              testId="obligations-section"
              accent="bg-emerald-50 text-emerald-700"
            >
              <ul className="space-y-3">
                {a.obligations.map((o, i) => (
                  <li
                    key={i}
                    className="flex gap-2.5 text-sm text-zinc-700 leading-relaxed"
                    data-testid={`obligation-${i}`}
                  >
                    <CheckCircle2
                      size={15}
                      strokeWidth={1.75}
                      className="text-emerald-500 shrink-0 mt-0.5"
                    />
                    <span>{o}</span>
                  </li>
                ))}
              </ul>
            </Card>

            <Card
              title="Detected risks"
              Icon={AlertTriangle}
              testId="risks-section"
              accent="bg-red-50 text-red-700"
            >
              <ul className="space-y-3">
                {a.detected_risks.map((r, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-sm leading-relaxed"
                    data-testid={`risk-${i}`}
                  >
                    <AlertTriangle
                      size={15}
                      strokeWidth={1.75}
                      className={`shrink-0 mt-0.5 ${
                        r.level === "High"
                          ? "text-red-500"
                          : r.level === "Medium"
                          ? "text-amber-500"
                          : "text-emerald-500"
                      }`}
                    />
                    <div className="min-w-0">
                      <p className="text-zinc-800">{r.text}</p>
                      <RiskBadge level={r.level} size="sm" />
                    </div>
                  </li>
                ))}
              </ul>
            </Card>

            <Card
              title="Recommended actions"
              Icon={Lightbulb}
              testId="actions-section"
              accent="bg-amber-50 text-amber-700"
            >
              <ul className="space-y-3">
                {a.recommended_actions.map((act, i) => (
                  <li
                    key={i}
                    className="flex gap-3 text-sm text-zinc-700 leading-relaxed"
                    data-testid={`action-${i}`}
                  >
                    <span className="font-display font-semibold text-zinc-400 shrink-0 w-5">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span>{act}</span>
                  </li>
                ))}
              </ul>
            </Card>
            </>
            )}
          </div>
        </div>
        </>
      )}
    </div>
  );
}

function Meta({ Icon, label, value, testId, citation }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon size={13} className="text-zinc-500" strokeWidth={1.75} />
        <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">{label}</p>
      </div>
      <p className="font-display text-lg font-semibold tracking-tight text-zinc-950">{value}</p>
      {citation && <DateCitation citation={citation} />}
    </div>
  );
}

function Card({ title, Icon, accent, children, testId }) {
  return (
    <section
      data-testid={testId}
      className="bg-white border border-zinc-200 rounded-xl p-6 hover:shadow-md transition-shadow"
    >
      <div className="flex items-center gap-2 mb-5">
        <div className={`w-7 h-7 rounded-md grid place-items-center ${accent}`}>
          <Icon size={14} strokeWidth={1.75} />
        </div>
        <h2 className="font-display font-semibold text-base tracking-tight">{title}</h2>
      </div>
      {children}
    </section>
  );
}
